<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import Flatpickr from 'svelte-flatpickr'
    import { writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { DateTime } from 'luxon'
    import { marked } from 'marked'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _, resolve, tgettext, type TGettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { session, simpleFetch, groups as ugroups } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toDate, toLuxon } from 'hydria/utils/time'

    import { DOMAIN_EXPIRATION_IMMINENT, DOMAIN_EXPIRATION_SOON } from 'wdom/constants'
    import { Breadcrumb } from 'breadcrumb'

    import VisualDate from 'hydria/components/Date.svelte'
    import PageList from 'hydria/components/PageList.svelte'
    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'
    import TokenAnchorProgress from '../Users/User/TokenAnchorProgress.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
        owner: 'string',
        published: '3state',
        publishable: '3state',
        use_slot: '3state',
        has_domain: '3state',
        has_dnssec_keys: '3state',
        ds_domain_linked: '3state',
        ns_are_local: '3state',
        ns_match_with_domain: '3state',
        ds_match_with_domain: '3state',
        expiration_lower: 'string',
        expiration_upper: 'string',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
        owner: string
        published: null | boolean
        publishable: null | boolean
        use_slot: null | boolean
        has_domain: null | boolean
        has_dnssec_keys: null | boolean
        ds_domain_linked: null | boolean
        ns_are_local: null | boolean
        ns_match_with_domain: null | boolean
        ds_match_with_domain: null | boolean
        expiration_lower: string
        expiration_upper: string
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
        owner: '',
        published: null,
        publishable: null,
        use_slot: null,
        has_domain: null,
        has_dnssec_keys: null,
        ds_domain_linked: null,
        ns_are_local: null,
        ns_match_with_domain: null,
        ds_match_with_domain: null,
        expiration_lower: '',
        expiration_upper: '',
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    // Purpose: set initial value on Flatpickr
    let expirationUpper: Date | undefined = $state()
    let expirationLower: Date | undefined = $state()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/zones' : `/zones?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/zones' : `/zones?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    let searchQueryStringJson: string | undefined = $state()
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (sOptions.q != '') sdata.text = sOptions.q
        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q', 'skip'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }
        searchQueryStringJson = encodeURIComponent(JSON.stringify(sdata))

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/zone/1/zone/search', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            for (const key of ['ctime', 'mtime']) {
                if (item[key]) item[key] = toLuxon(item[key])
            }
            if (item.expiration) {
                item.expiration = toLuxon(item.expiration)
                if (item.expiration.diff(now) < DOMAIN_EXPIRATION_IMMINENT) {
                    item.expirationStatus = 'imminent'
                } else if (item.expiration.diff(now) < DOMAIN_EXPIRATION_SOON) {
                    item.expirationStatus = 'soon'
                } else {
                    item.expirationStatus = 'clean'
                }
            } else {
                item.expirationStatus = 'unknown'
            }

            item.idnaUnicode = toUnicode(item.idna)

            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)
    // svelte-ignore state_referenced_locally
    let ownerText = writable(sOptions.owner)

    function loadIntoForm() {
        $text = sOptions.q
        $ownerText = sOptions.owner
        if (sOptions.expiration_lower) {
            expirationLower = toDate(sOptions.expiration_lower)
        }
        if (sOptions.expiration_upper) {
            expirationUpper = toDate(sOptions.expiration_upper)
        }
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    function onTimeout() {
        if ($text !== sOptions.q) sOptions.q = $text
        if ($ownerText !== sOptions.owner) sOptions.owner = $ownerText
        textTimer = null
        setQueryString(sOptions)
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue === sOptions.q) {
                return
            }
            textTimer = setTimeout(onTimeout, DELAY_MS)
        }),
    )
    unsubs.push(
        ownerText.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue === sOptions.owner) {
                return
            }
            textTimer = setTimeout(onTimeout, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        sOptions.owner = $ownerText
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    const flatOpts_lower: any = {
        element: '#expiration_lower',
    }
    const flatOpts_upper: any = {
        element: '#expiration_upper',
    }

    let helpHtml: string | undefined = $state(undefined)
    function showHelp(text: TGettext) {
        return () => {
            helpHtml = marked.parse($resolve(text)) as string
        }
    }
    function hideHelp() {
        return () => {
            helpHtml = undefined
        }
    }
</script>

<Breadcrumb crumbs={[{ txt: tgettext('Zones') }]} />

<div class="card mb-4">
    <div class="card-title text-primary">{$_('Search parameters')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div class="zone-list-filters">
                <div class="zlf-help">
                    {#if helpHtml}
                        <div transition:fade={{ duration: 200 }}>{@html helpHtml}</div>
                    {/if}
                </div>
                <div class="zlf-text">
                    <div class="input-group">
                        <input type="text" class="form-control" id="idna-text" placeholder={$_('Zone name (wildcard: *)')} bind:this={mainInput} bind:value={$text} />
                        {#if $text}
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                        <MiniKeyboard eid={'idna-text'} />
                        <button class="btn btn-primary search" type="submit"><Icon inline icon="fa:search" /></button>
                    </div>
                </div>
                <div class="zlf-owner">
                    <div class:input-group={!!$ownerText}>
                        <input type="text" class="form-control" placeholder={$_('Owner ID')} bind:value={$ownerText} />
                        {#if $ownerText}
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $ownerText = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                    </div>
                </div>

                <div class="zlf-date-min">
                    <Flatpickr
                        options={flatOpts_lower}
                        value={expirationLower}
                        formattedValue={sOptions.expiration_lower}
                        on:valueUpdate={(e) => {
                            sOptions.expiration_lower = e.detail[1]
                            setQueryString(sOptions)
                        }}
                        dateFormat="Y-m-d"
                        element="#expiration_lower"
                    >
                        <div id="expiration_lower" class="input-group">
                            <input type="text" class="form-control" data-input placeholder="Expiration min" />
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                onclick={() => {
                                    sOptions.expiration_lower = ''
                                    setQueryString(sOptions)
                                }}
                                data-clear
                                disabled={!sOptions.expiration_lower}><Icon inline icon="fa:times" /></button
                            >
                        </div>
                    </Flatpickr>
                </div>
                <div class="zlf-date-max">
                    <Flatpickr
                        options={flatOpts_upper}
                        value={expirationUpper}
                        formattedValue={sOptions.expiration_upper}
                        on:valueUpdate={(e) => {
                            sOptions.expiration_upper = e.detail[1]
                            setQueryString(sOptions)
                        }}
                        dateFormat="Y-m-d"
                        element="#expiration_upper"
                    >
                        <div id="expiration_upper" class="input-group">
                            <input type="text" class="form-control" data-input placeholder="Expiration max" />
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                onclick={() => {
                                    sOptions.expiration_upper = ''
                                    setQueryString(sOptions)
                                }}
                                data-clear
                                disabled={!sOptions.expiration_upper}><Icon inline icon="fa:times" /></button
                            >
                        </div>
                    </Flatpickr>
                </div>

                <div class="zlf-published">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>{$_('Publiée')}</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.published === true}
                            onclick={() => {
                                sOptions.published = sOptions.published === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **est publiée**.'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.published === false}
                            onclick={() => {
                                sOptions.published = sOptions.published === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **n’est pas publiée**.'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-publishable">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>{$_('Publiable')}</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.publishable === true}
                            onclick={() => {
                                sOptions.publishable = sOptions.publishable === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **peut être publiée**.'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.publishable === false}
                            onclick={() => {
                                sOptions.publishable = sOptions.publishable === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **ne peut pas être publiée**.'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-slot">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>{$_('Flottante')}</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.use_slot === true}
                            onclick={() => {
                                sOptions.use_slot = sOptions.use_slot === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone est **publiée** et utilise un **« Slot »** au lieu d’une liaison à un domaine.'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.use_slot === false}
                            onclick={() => {
                                sOptions.use_slot = sOptions.use_slot === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **n’est pas publiée**, _ou_ n’utilise **pas de « Slot »** lorsque publiée.'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-domain">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>{$_('Domaine')}</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_domain === true}
                            onclick={() => {
                                sOptions.has_domain = sOptions.has_domain === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Un _domaine du même nom_ **existe** sur WebDomaine.'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_domain === false}
                            onclick={() => {
                                if (sOptions.ns_match_with_domain != null) sOptions.ns_match_with_domain = null
                                if (sOptions.ns_are_local != null) sOptions.ns_are_local = null
                                sOptions.has_domain = sOptions.has_domain === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Un _domaine du même nom_ **n’existe pas** sur WebDomaine.'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-local">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>NS loc</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating || sOptions.has_domain !== true}
                            class:active={sOptions.ns_are_local === true}
                            onclick={() => {
                                sOptions.ns_are_local = sOptions.ns_are_local === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les serveurs DNS sont **locaux** (chez WebDomaine).'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating || sOptions.has_domain !== true}
                            class:active={sOptions.ns_are_local === false}
                            onclick={() => {
                                if (sOptions.ns_match_with_domain != null) sOptions.ns_match_with_domain = null
                                sOptions.ns_are_local = sOptions.ns_are_local === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les serveurs DNS **ne sont pas locaux** (chez WebDomaine).'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-match">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>NS Z=D</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating || sOptions.ns_are_local !== true}
                            class:active={sOptions.ns_match_with_domain === true}
                            onclick={() => {
                                sOptions.ns_match_with_domain = sOptions.ns_match_with_domain === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les serveurs DNS de la zone et du domaine **correspondent**.'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating || sOptions.ns_are_local !== true}
                            class:active={sOptions.ns_match_with_domain === false}
                            onclick={() => {
                                sOptions.ns_match_with_domain = sOptions.ns_match_with_domain === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les serveurs DNS de la zone et du domaine **ne correspondent pas**.'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-dnssec">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>DNSSEC</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_dnssec_keys === true}
                            onclick={() => {
                                sOptions.has_dnssec_keys = sOptions.has_dnssec_keys === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **a des clés** de signature DNSSEC.'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_dnssec_keys === false}
                            onclick={() => {
                                sOptions.has_dnssec_keys = sOptions.has_dnssec_keys === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('La zone **n’a pas de clés** de signature DNSSEC.'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-dssync">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>DS Sync</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.ds_domain_linked === true}
                            onclick={() => {
                                sOptions.ds_domain_linked = sOptions.ds_domain_linked === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les inscriptions DS **sont synchronisées** Zone → Domaine (implique qu’un domaine existe chez WebDomaine).'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.ds_domain_linked === false}
                            onclick={() => {
                                sOptions.ds_domain_linked = sOptions.ds_domain_linked === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les inscriptions DS **ne sont pas synchronisées** entre la zone et le domaine. (Implique l’existence du domaine chez WebDomaine).'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
                <div class="zlf-dsmatch">
                    <div class="btn-group">
                        <button class="btn btn-secondary" type="button" disabled>DS Z=D</button>
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.ds_match_with_domain === true}
                            onclick={() => {
                                sOptions.ds_match_with_domain = sOptions.ds_match_with_domain === true ? null : true
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les inscriptions DS **correspondent** entre la zone et le domaine (implique l’existence du domaine chez WebDomaine).'))}><Icon inline icon="fa:check-square-o" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.ds_match_with_domain === false}
                            onclick={() => {
                                sOptions.ds_match_with_domain = sOptions.ds_match_with_domain === false ? null : false
                                setQueryString(sOptions)
                            }}
                            onmouseleave={hideHelp()}
                            onmouseenter={showHelp(tgettext('Les inscriptions DS **ne correspondent pas** entre la zone et le domaine (implique l’existence du domaine chez WebDomaine).'))}><Icon inline icon="fa:square-o" /></button
                        >
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Zones')}</div>

        {#if $results.length}
            <div class="card-body">
                <table class="zone-list-table table table-striped mb-0" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th>{$_('Domaine / Zone')}</th>
                            <th class="text-center">{$_('Serial')}</th>
                            <th class="text-center">{$_('Publiée')}</th>
                            <th class="text-center">{$_('Publishable')}</th>
                            <th class="text-center">{$_('Use slot')}</th>
                            <th class="text-center">{$_('Last modified')}</th>
                            <th class="text-center">{$_('Domain')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.idna)}
                            <tr class="expiration-{item.expirationStatus} zone-exp-{item.zoneExpirationStatus}">
                                <td><a use:sanchor href={spath(`/zone/${item.idna}`)}>{item.idnaUnicode}</a></td>
                                <td class="text-center">{item.revision}</td>
                                <td class="text-center" class:text-danger={!item.published}><Icon icon={item.published ? 'fa:check-square-o' : 'fa:square-o'} /></td>
                                <td class="text-center" class:text-danger={item.publishable && item.permanent}>
                                    <Icon icon={item.publishable ? 'fa:check-square-o' : 'fa:square-o'} />
                                    {#if item.publishable}
                                        <span class="nowrap">
                                            {#if item.permanent}
                                                ∞
                                            {:else if !item.expiration}
                                                –
                                            {:else}
                                                <VisualDate value={item.expiration} format="h-table-date" />
                                            {/if}
                                        </span>
                                    {/if}
                                </td>
                                <td class="text-center"><Icon icon={item.use_slot ? 'fa:check-square-o' : 'fa:square-o'} /></td>
                                <td class="text-center"><VisualDate value={item.mtime} format="h-table-s" /></td>
                                <td class="text-center">
                                    {#if !item.has_domain}<span>non</span>
                                    {:else}
                                        <a use:sanchor href={spath(`/domain/${item.idna}`)} class:text-warning={!item.ns_on_webdomaine} class:text-danger={item.ns_on_webdomaine && !item.ns_same_as_domain} class:text-success={item.ns_on_webdomaine && item.ns_same_as_domain}>
                                            {#if !item.ns_on_webdomaine}NS externes
                                            {:else if !item.ns_same_as_domain}NS diffèrent
                                            {:else}OK
                                            {/if}
                                        </a>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer text-bg-light">
            <div class="row">
                <div class="col-sm-5 create-new">
                    {#if $ownerText && $ownerText.length == 36}
                        <a use:sanchor href={spath(`/zone/new/${$ownerText}`)} class="btn btn-primary btn-sm">Create a zone</a>
                    {/if}
                </div>
                <div class="col-sm-7">
                    <PageList
                        loading={updating}
                        {nItems}
                        limit={LIMIT}
                        skip={sOptions.skip}
                        setSkip={(e) => {
                            sOptions.skip = e
                            setQueryString(sOptions)
                        }}
                    />
                </div>
            </div>
        </div>
    </div>

    {#if $ugroups.has('wdom-user-admin') && $ugroups.has('global-ecom-admin')}
        <div class="card mt-4">
            <div class="card-title text-secondary">{$_('Administrative tools')}</div>
            <div class="card-body">
                <TokenAnchorProgress class="btn btn-outline-secondary" href="/json/zone/1/all-zones/tsv">List all zones in TSV format</TokenAnchorProgress>
            </div>
        </div>
    {/if}
{/if}
